import {
  SelectionTree,
  TreeNodeDefinition,
} from '@trimble/ui-components/solidjs';
import { t } from '@trimble/i18n';
import { readModusInputChangeValue } from '@trimble/utilities';
import {
  Component,
  JSX,
  ParentComponent,
  Show,
  createMemo,
  onCleanup,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import type { MeasurementSurface } from '../../types/surface';
import {
  createHoverFocusState,
  type HoverFocusState,
} from './createHoverFocusState';

export interface SurfaceSelectComboboxViewProps {
  testId: string;
  label: string;
  disabled?: boolean;
  isOpen: boolean;
  filterText: string;
  selectedDisplayName: string;
  isSelectedUnavailable?: boolean;
  dropdownId: string;
  panelStyle: JSX.CSSProperties;
  treeNodes: TreeNodeDefinition<MeasurementSurface>[];
  defaultExpandIds: string[];
  selectedSurfaceId?: string;
  setRootRef(el: HTMLDivElement): void;
  setPanelRef(el: HTMLElement | null): void;
  onTriggerActivate(): void;
  onFilterTextChange(value: string): void;
  onNodeSelect(
    nodeId: string,
    node: TreeNodeDefinition<MeasurementSurface>
  ): void;
}

const noop = (): void => undefined;

const WarningInteractionLifetime: ParentComponent<{
  interaction: HoverFocusState;
}> = (props) => {
  onCleanup(() => props.interaction.reset());
  return <>{props.children}</>;
};

export const SurfaceSelectComboboxView: Component<
  SurfaceSelectComboboxViewProps
> = (props) => {
  const inputValue = (): string =>
    props.isOpen ? props.filterText : props.selectedDisplayName;

  const warningInteraction = createHoverFocusState();
  const isWarningVisible = createMemo(
    () => Boolean(props.isSelectedUnavailable) && !props.isOpen
  );
  const isWarningSuppressingNameTooltip = createMemo(
    () => isWarningVisible() && warningInteraction.isActive()
  );

  const openIfNeeded = (): void => {
    if (props.disabled) {
      return;
    }
    props.onTriggerActivate();
  };

  return (
    <div
      ref={props.setRootRef}
      class="relative w-full"
      data-testid={props.testId}
    >
      <div
        class="relative"
        role="combobox"
        aria-expanded={props.isOpen}
        aria-haspopup="listbox"
        aria-disabled={props.disabled}
        aria-label={props.label}
        data-testid={`${props.testId}-trigger`}
      >
        <modus-wc-tooltip
          class="block w-full min-w-0"
          content={props.selectedDisplayName}
          position="bottom"
          disabled={
            props.isOpen ||
            !props.selectedDisplayName ||
            isWarningSuppressingNameTooltip()
          }
          attr:data-testid={`${props.testId}-name-tooltip`}
        >
          <modus-wc-text-input
            class="block w-full min-w-0 [&_input]:min-w-0 [&_input]:!opacity-100 [&_.modus-wc-input]:box-border [&_.modus-wc-input-label]:!mb-0"
            label={props.label}
            value={inputValue()}
            placeholder={t('general.search')}
            bordered={true}
            size="md"
            type="search"
            auto-complete="off"
            disabled={props.disabled}
            include-search={true}
            include-clear={props.isOpen}
            custom-class={isWarningVisible() ? '!pr-10' : undefined}
            onClick={openIfNeeded}
            on:inputFocus={openIfNeeded}
            on:inputChange={(event) => {
              props.onFilterTextChange(readModusInputChangeValue(event));
            }}
          />
        </modus-wc-tooltip>
        <Show when={isWarningVisible()}>
          <WarningInteractionLifetime interaction={warningInteraction}>
            <modus-wc-tooltip
              class="absolute right-2 bottom-0 flex items-center h-[var(--modus-wc-size-md)]"
              content={t('measurements.volumetric.deletedTooltip')}
              position="bottom"
              tooltip-id={`${props.testId}-deleted-description`}
              attr:data-testid={`${props.testId}-deleted-tooltip`}
            >
              <button
                type="button"
                class="flex items-center justify-center w-6 h-[var(--modus-wc-size-md)] shrink-0 cursor-help text-warning"
                aria-label={t('measurements.volumetric.deletedTooltip')}
                aria-describedby={`${props.testId}-deleted-description`}
                data-testid={`${props.testId}-deleted-warning`}
                {...warningInteraction.handlers}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                <modus-wc-icon name="warning" variant="outlined" size="sm" />
              </button>
            </modus-wc-tooltip>
          </WarningInteractionLifetime>
        </Show>
      </div>

      <Show when={props.isOpen}>
        <Portal>
          <section
            class="fixed z-dropdown pointer-events-auto rounded border border-[var(--modus-wc-color-base-200,#e0e1e9)] bg-[var(--modus-wc-color-base-page,#fff)] shadow-md"
            style={props.panelStyle}
            data-testid={`${props.testId}-menu`}
            data-dropdown-id={props.dropdownId}
            ref={props.setPanelRef}
            role="listbox"
            aria-label={props.label}
          >
            <div
              class="max-h-64 overflow-x-hidden overflow-y-auto overscroll-contain p-2"
              data-testid={`${props.testId}-tree`}
            >
              <SelectionTree
                multiSelect={false}
                nodes={props.treeNodes}
                childrenLoadStatus="success"
                defaultExpandIds={props.defaultExpandIds}
                filterText={props.filterText}
                isNodeLoading={() => false}
                isNodeVisible={() => false}
                onVisibilityToggle={noop}
                isNodeSelected={(nodeId) => nodeId === props.selectedSurfaceId}
                onNodeSelect={props.onNodeSelect}
              />
            </div>
          </section>
        </Portal>
      </Show>
    </div>
  );
};
